import {DataTable} from "../DataTable/DataTable";
import {TagDefinitionEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useTagDefinitionDataTableFilter} from "../TagDefinitionDataTable/useTagDefinitionDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {useNavigate} from "react-router-dom";
import {useTagDefinitionAction} from "../../menu/TagDefinitionActionMenu/useTagDefinitionAction";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";
import {YesNoText} from "../../text/YesNoText/YesNoText";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {ColorSwatch, Group, Text} from "@mantine/core";

const EMPTY_ARRAY: TagDefinitionEntity[] = []

export type TagDefinitionDataTableProps = EntityDataTableProps<TagDefinitionEntity>

export const TagDefinitionDataTable = (props: TagDefinitionDataTableProps) => {
  const {
    records: defs = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {filteredRecords, filterConfig} = useTagDefinitionDataTableFilter({
    records: defs
  })

  const sortConfig = useMemo(() => {
    return {
      "label": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.label?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'label', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    onTagDefinitionActionClick
  } = useTagDefinitionAction()

  const onViewActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: TagDefinitionEntity) => {
    e.stopPropagation()
    navigate(`/tags/definitions/profile?id=${record.id}`)
  }, [navigate])

  const onEditActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: TagDefinitionEntity) => {
    e.stopPropagation()
    onTagDefinitionActionClick(record, 'EDIT')
  }, [onTagDefinitionActionClick])

  const onDeleteActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: TagDefinitionEntity) => {
    e.stopPropagation()
    onTagDefinitionActionClick(record, 'DELETE')
  }, [onTagDefinitionActionClick])

  const columns = useMemo(() => {
    const values: DataTableColumn<TagDefinitionEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "label"),
        title: t("common.entity.tagdefinition.table.column.label", "Label"),
        accessor: 'label',
        width: 100,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "sourceKind"),
        title: t("common.entity.tagdefinition.table.column.sourcekind", "Source"),
        accessor: 'sourceKind',
        width: 100,
        sortable: true,
        render: ({sourceKind}) => {
          return <FormattedDatabaseCode value={sourceKind}/>
        },
      },
      {
        title: t("common.entity.tagdefinition.table.column.color", "Color"),
        accessor: 'colorHex',
        width: 100,
        sortable: true,
        render: ({colorHex}) => {
          if (colorHex) {
            return (
              <Group spacing="xs">
                <ColorSwatch color={colorHex}/>
                <Text size="xs">{colorHex}</Text>
              </Group>
            )
          }
          return undefined
        }
      },
      {
        title: t("common.entity.tagdefinition.table.column.donotuse", "Enabled"),
        accessor: 'doNotUse',
        width: 100,
        sortable: true,
        render: ({doNotUse}) => {
          return <YesNoText value={!doNotUse}/>
        }
      },
      {
        title: '',
        accessor: "actions",
        width: 50,
        textAlignment: 'right',
        render: (def) => (
          <EntityActionGroup
            permissionPrefix="tagdefinition"
            view={false}
            onViewActionClick={(e) => onViewActionClick(e, def)}
            onEditActionClick={(e) => onEditActionClick(e, def)}
            onDeleteActionClick={(e) => onDeleteActionClick(e, def)}/>
        )
      }
    ]

    return values
  }, [filterConfig, onDeleteActionClick, onEditActionClick, onViewActionClick, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}
